import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Text } from '@chakra-ui/react';
import { useRef } from 'react';
import { AppDialog, DialogButton } from '../common/AppDialog';
import {
    AutosaveSnapshot, clearAutosaveFile, getRecoverySnapshot, startAutosave,
} from '../../utils/autosave';
import { createRecoveryReport, summarizeRecoveryReport, writeRecoveryLog } from '../../services/fileSystem/recoveryReport';
import { sha256FileHex } from '../../services/fileSystem/integrity';
import { notify } from '../../utils/notify';

/**
 * Crash Recovery Host
 *
 * Mounted once by ProjectDialogsHost. On boot it scans the autosave folder
 * (`yap/autosave/*.sv$` — appData on desktop, mirrored OPFS in the browser)
 * plus the legacy localStorage slot for a snapshot the previous session
 * left behind, and offers to restore the newest one (Faz 9.2/9.3). A
 * successful restore writes a recovery report log (Faz 9.4). It also arms
 * the periodic autosave writer, building the envelope through a deferred
 * import so the I/O layer stays out of the boot render graph.
 */
export function RecoveryHost() {
    const { t } = useTranslation();
    const [snapshot, setSnapshot] = useState<AutosaveSnapshot | null>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        let cancelled = false;
        void getRecoverySnapshot().then((pending) => {
            if (!cancelled && pending) setSnapshot(pending);
        });
        void import('../../utils/ProjectFileIO')
            .then(({ captureAutosaveSnapshot }) => startAutosave(captureAutosaveSnapshot))
            .catch(() => { /* autosave is best-effort */ });
        return () => { cancelled = true; };
    }, []);

    const handleRestore = async () => {
        if (!snapshot) return;
        const startedAt = Date.now();
        const { restoreFromSnapshot } = await import('../../utils/ProjectFileIO');
        const ok = await restoreFromSnapshot(snapshot.envelopeText, snapshot.displayName);
        // Recovery report (Faz 9.4): scanned/removed counts come from the
        // snapshot envelope itself — the restore pipeline is the scan.
        let entitiesScanned = 0;
        let diagnostics: string[] = [];
        try {
            const parsed = JSON.parse(snapshot.envelopeText) as {
                cadScene?: { objects?: unknown[] };
                project?: { elements?: unknown[] };
            };
            entitiesScanned = (parsed.cadScene?.objects?.length ?? 0)
                + (Array.isArray(parsed.project?.elements) ? parsed.project.elements.length : 0);
            diagnostics = [`restored from autosave ${snapshot.savedAt}`];
        } catch {
            diagnostics = ['snapshot envelope could not be re-parsed for counts'];
        }
        const sizeBytes = snapshot.envelopeText.length;
        const sha = await sha256FileHex(new TextEncoder().encode(snapshot.envelopeText));
        const report = createRecoveryReport({
            status: ok ? 'recovered' : 'failed',
            fileName: snapshot.displayName,
            sizeBytes,
            sourceSha256: sha,
            entitiesScanned,
            entitiesRemoved: 0,
            diagnostics: ok ? diagnostics : ['restore pipeline rejected the snapshot'],
            startedAt,
        });
        await writeRecoveryLog(report);
        notify(summarizeRecoveryReport(report), ok ? 'info' : 'warning');
        setSnapshot(null);
    };

    const handleDiscard = async () => {
        await clearAutosaveFile(snapshot?.displayName);
        setSnapshot(null);
    };

    return (
        <AppDialog
            isOpen={snapshot !== null}
            onClose={() => void handleDiscard()}
            title={t('dialogs.ui.recoverUnsavedWork')}
            variant="alert"
            initialFocusRef={cancelRef}
            footer={
                <>
                    <DialogButton ref={cancelRef} variant="secondary" onClick={() => void handleDiscard()}>
                        {t('dialogs.ui.discard')}
                    </DialogButton>
                    <DialogButton variant="primary" onClick={() => void handleRestore()}>
                        {t('dialogs.ui.restore')}
                    </DialogButton>
                </>
            }
        >
            <Text mb={2}>
                The previous session ended without saving{' '}
                <Text as="span" fontWeight="semibold">{snapshot?.displayName}</Text>.
            </Text>
            <Text fontSize="sm" color="var(--color-text-muted)">
                Autosave snapshot from {snapshot ? new Date(snapshot.savedAt).toLocaleString() : ''}.
            </Text>
        </AppDialog>
    );
}
